/**
 * 認証状態のストア。
 *
 * 状態ライブラリ（zustand 等）はこのアプリの依存に無いので、React 18 の
 * `useSyncExternalStore` で最小限のストアを自前で持つ。認証状態はルーターの
 * `beforeLoad`（React の外）からも読む必要があるため、Context ではなく
 * モジュールスコープのストアにしている。
 */
import * as React from 'react';
import type { PortalUser } from '../lib/api-types.js';

export type AuthStatus =
  /** 起動直後。silent refresh の結果がまだ分からない。 */
  | 'unknown'
  | 'authenticated'
  | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  user: PortalUser | null;
}

let state: AuthState = { status: 'unknown', user: null };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export const authStore = {
  getState(): AuthState {
    return state;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  setUser(user: PortalUser): void {
    state = { status: 'authenticated', user };
    emit();
  },
  setAnonymous(): void {
    // 同じ状態への遷移で再描画を起こさない（ログイン画面が点滅する）。
    if (state.status === 'anonymous' && state.user === null) return;
    state = { status: 'anonymous', user: null };
    emit();
  },
  patchUser(patch: Partial<PortalUser>): void {
    if (!state.user) return;
    state = { status: state.status, user: { ...state.user, ...patch } };
    emit();
  },
};

export function useAuth(): AuthState {
  return React.useSyncExternalStore(authStore.subscribe, authStore.getState, authStore.getState);
}

/** ログイン中のユーザー。未ログインでは呼ばない前提の画面で使う。 */
export function useCurrentUser(): PortalUser {
  const { user } = useAuth();
  if (!user) throw new Error('ログイン前の画面で useCurrentUser を呼び出しています');
  return user;
}

/** 権限判定。ロール名の直接比較を画面に散らさないため、判定はここに集約する。 */
export function canManageOrg(user: PortalUser | null): boolean {
  return user?.role === 'org_owner' || user?.role === 'org_admin';
}

/**
 * 権限コードの保持判定。
 *
 * ロール名での分岐（`role === 'org_admin'`）を画面に書くと、ロールと権限の
 * 対応が変わったときに全画面を直すことになる。`/me` が返す `permissions` を
 * 直接見る。**サーバー側の判定が正**で、これは画面の出し分けのためだけの判定。
 */
export function hasPermission(user: PortalUser | null, permission: string): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

/** 複数のうち 1 つでも持っているか（ナビの表示判定に使う）。 */
export function hasAnyPermission(user: PortalUser | null, permissions: readonly string[]): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

/** 画面から使う権限判定フック。`ORG_PERMISSION` の値をそのまま渡す。 */
export function usePermission(permission: string): boolean {
  const { user } = useAuth();
  return hasPermission(user, permission);
}

export function canManageBilling(user: PortalUser | null): boolean {
  return user?.role === 'org_owner';
}

export function canManageMembers(user: PortalUser | null): boolean {
  return user?.role === 'org_owner';
}
